import { Controller } from "@hotwired/stimulus";
import { countries } from "components/common/countries";

// Replaces the jQuery-built country/phone-code picker in
// step2/_booking_form.html.erb (a custom `.select-box`/`.selected-option`
// dropdown, not a native <select>). Reuses the same full country/dial-code/
// flag-image dataset the old picker did (components/common/countries.js) --
// an earlier version of this controller hand-picked a handful of countries
// with guessed flag numbers, which 404'd (only Sweden's 135 was ever
// actually verified). Default country is derived from the current domain's
// TLD, same as the old inline script, falling back to Sweden. Builds
// `you[mobile]` on submit by combining the selected dial code with the
// local number (leading zeros stripped) -- same contract
// step2_submit_controller.js's serializeForm() call expects.
//
// Country names are localized per gate, the way the old picker was: the
// names in countries.js are English, and localized_countries.js carries
// translated ones keyed by language and ISO alpha-2 (the same lookup
// packs/mobile/step2.js still does). It covers da, de, es, fi, fr, nb, nl,
// pl and sv; el, en, is, it and ro have no entry and keep the English
// name, which for en is the right answer anyway and for the rest is what
// the old code did too -- except that it indexed blindly and would throw
// on a missing locale, so this looks both up defensively.
//
// That file is 97KB, and loading it is deferred twice over: it is a
// dynamic import, so it is its own chunk rather than part of every page's
// bundle, and the chunk is only requested when someone actually opens a
// picker. The list renders with the English names first and re-renders
// when the translations land, so the dropdown is never briefly empty on a
// slow connection.
export default class extends Controller {
  static targets = ["input", "hiddenMobile", "flag", "dialCode", "options", "list", "search"];

  connect() {
    this.selected = this.defaultCountry();
    this.applySelection();
    this.boundClose = this.closeOnOutsideClick.bind(this);
    document.addEventListener("click", this.boundClose);
  }

  disconnect() {
    document.removeEventListener("click", this.boundClose);
  }

  // Resolves once per page and is shared by every picker instance on it
  // (step2 renders one per traveller plus one for the contact) -- the
  // promise is cached on the class, so the chunk is fetched a single time.
  localizedCountries() {
    const ctor = this.constructor;
    if (ctor.localizedPromise) return ctor.localizedPromise;

    const language = ((window.gon && window.gon.locale) || "").slice(0, 2).toLowerCase();

    ctor.localizedPromise = import(
      /* webpackChunkName: "localized-countries" */ "components/common/localized_countries"
    )
      .then((module) => {
        const translated = module.localized_countries[language];
        if (!translated) return countries;

        const byAlpha2 = new Map(translated.map((entry) => [entry.alpha2, entry.name]));
        const localized = countries.map((country) => ({
          ...country,
          name: byAlpha2.get(country.code.toLowerCase()) || country.name,
        }));

        // countries.js opens with seven countries in deliberate priority
        // order and is alphabetical by ENGLISH name after that, which
        // stops being alphabetical once the names are translated
        // ("Tyskland" filed under G, "Tjeckien" under C). The priority
        // head keeps its order; the rest is sorted in the visitor's own
        // language.
        const priority = localized.slice(0, 7);
        const rest = localized
          .slice(7)
          .sort((a, b) => a.name.localeCompare(b.name, language || undefined));
        return priority.concat(rest);
      })
      .catch(() => countries);

    return ctor.localizedPromise;
  }

  defaultCountry() {
    const tld = window.location.hostname.split(".").pop().toUpperCase();
    return countries.find((c) => c.code === tld) || countries.find((c) => c.code === "SE") || countries[0];
  }

  // Built on first open, not in connect().
  //
  // This list is ~240 countries and step2 renders a picker per traveller
  // plus one for the contact, so rendering it up front put roughly 480 list
  // items in the DOM before anyone touched the field -- and, because an
  // eager <img> is fetched whether or not anything can see it, pulled all
  // ~240 flag PNGs over the wire on every step2 load. That was 239 of the
  // page's 266 requests, for a dropdown most people never open.
  //
  // loading="lazy" is the other half: a lazy image inside a display:none
  // parent is not fetched at all, and once the dropdown opens only the
  // dozen or so flags actually scrolled into view load, the rest as you
  // scroll. width/height are the assets' real intrinsic size (every flag
  // under app/assets/images/flags is 20x12, checked) so a row does not
  // reflow as its flag arrives; the class still controls the rendered size. alt is empty because the country name is right next to
  // it -- the flag is decoration, not information.
  renderList(list) {
    if (!this.hasListTarget) return;
    this.listRendered = true;
    this.listTarget.innerHTML = list
      .map(
        (c) => `<li><button type="button" class="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm hover:bg-gray-50" data-code="${c.code}">
        <img src="/assets/flags/${c.flag_code}" class="h-3 w-4" width="20" height="12" loading="lazy" decoding="async" alt="" /> <span>${c.phone}</span> <span class="text-gray-500">${c.name}</span>
      </button></li>`
      )
      .join("");
  }

  toggle() {
    if (!this.listRendered) {
      this.renderList(countries);
      this.localizedCountries().then((list) => {
        this.countries = list;
        if (!this.hasSearchTarget || !this.searchTarget.value.trim()) this.renderList(list);
      });
    }
    this.optionsTarget.classList.toggle("hidden");
  }

  // Whatever names are current: translated once they have loaded, English
  // until then.
  countryList() {
    return this.countries || countries;
  }

  search() {
    // Filters the localized names, i.e. the ones on screen -- a Swedish
    // visitor types "Tyskland", not "Germany". Reachable before toggle()
    // only if something focuses the box programmatically; renderList sets
    // the rendered flag either way.
    const query = this.searchTarget.value.trim().toLowerCase();
    const list = this.countryList();
    this.renderList(query ? list.filter((c) => c.name.toLowerCase().includes(query)) : list);
  }

  select(event) {
    const code = event.target.closest("[data-code]")?.dataset.code;
    const country = countries.find((c) => c.code === code);
    if (!country) return;
    this.selected = country;
    this.applySelection();
    this.optionsTarget.classList.add("hidden");
  }

  applySelection() {
    if (this.hasFlagTarget) this.flagTarget.src = `/assets/flags/${this.selected.flag_code}`;
    if (this.hasDialCodeTarget) this.dialCodeTarget.textContent = this.selected.phone;
  }

  // Called right before the surrounding form submits (see
  // step2_submit_controller.js) to populate the real submitted field.
  writeHiddenField() {
    const local = (this.inputTarget.value || "").replace(/^0+/, "").trim();
    this.hiddenMobileTarget.value = `${this.selected.phone}${local}`;
  }

  closeOnOutsideClick(event) {
    if (this.hasOptionsTarget && !this.element.contains(event.target)) {
      this.optionsTarget.classList.add("hidden");
    }
  }
}
